/**
 * Wire types for Fun deposit progress. Every type here is our own shape unless its JSDoc names
 * Fun, 1Click or Base as the source. The server's zod schemas and the web's zod mirror each
 * `satisfies` these, so a field missing or mistyped on either side fails to compile.
 */

import type { XchainStage } from "./xchain";

export const FUN_PAYMENT_STEPS = ["submitted", "received", "sent", "available"] as const;
export type FunPaymentStep = (typeof FUN_PAYMENT_STEPS)[number];

export type FunPaymentStepStatus = "done" | "active" | "pending" | "failed";

/**
 * `under_review`: Fun may be holding the funds while it reviews the deposit address.
 * `unresolved`: Fun reported an error but a refund state its SDK reads as credited.
 */
export const FUN_PAYMENT_STATES = [
  "in_progress",
  "delayed",
  "under_review",
  "needs_support",
  "failed",
  "refunded",
  "unresolved",
  "done",
] as const;
export type FunPaymentState = (typeof FUN_PAYMENT_STATES)[number];

export function isFinalFunPaymentState(state: FunPaymentState): boolean {
  return state === "done" || state === "failed" || state === "refunded" || state === "unresolved";
}

export function isOpenFunPaymentState(state: FunPaymentState): boolean {
  return !isFinalFunPaymentState(state);
}

export type FunPaymentMethod = "card" | "connect_wallet";

export type FunPaymentEntry = "card" | "crypto";

export type FunPaymentNextAction = "wait" | "contact_support" | "confirm_deposit" | "dismiss";

/** Whose data withdrew a proof: `near` is 1Click's sweep data, `decibel` our own check. */
export type FunCorrectionSource = "fun" | "base" | "near" | "decibel";

/**
 * `exact`: a Connect Wallet confirmation named the payment's direct execution.
 * `count`: oldest-first matching against Fun records inside a time window.
 */
export type FunPaymentPairing = "exact" | "count";

/** Mirrors our own `/api/xchain/deposits` row, so the deposit action keeps its contract. */
export interface FunPaymentDelivery {
  id: string;
  destinationAssetKey: string;
  stage: XchainStage;
  /** 1Click's raw status for the sweep. */
  status: string;
  updatedAt: string | null;
  amountOutFormatted: string | null;
  amountOutUsd: string | null;
  withdrawFeeFormatted: string | null;
  withdrawFeeUsd: string | null;
  destinationTxHash: string | null;
  destinationSymbol: string;
  destinationFaAddress: string;
  destinationDecimals: number;
  sweepable: boolean;
}

export interface FunPaymentItem {
  /** The key cards match on: our payment id once one backs the item, else `fun:<earliest ref>`. */
  id: string;
  /** Every id the item has had, so a card keeps its slot when `id` changes. */
  aliases: string[];
  entry: FunPaymentEntry | null;
  /** The user's own checkout; null when no payment of ours paired with it. */
  method: FunPaymentMethod | null;
  /** How our payment was matched to Fun's records; null while unmatched. */
  pairing: FunPaymentPairing | null;
  /** Only our own widget callback backs the item; no Fun, Base or NEAR evidence yet. */
  provisional: boolean;
  /** What support and the lookup take: our payment id, else Fun's record ref. */
  supportRef: string;
  steps: Record<FunPaymentStep, { status: FunPaymentStepStatus; at: string | null }>;
  current: FunPaymentStep;
  state: FunPaymentState;
  /** A step or failure that evidence once proved and a correction since withdrew. */
  retraction: { of: FunPaymentStep | "failed"; at: string; source: FunCorrectionSource } | null;
  /** A Base reorg rolled back the sighting behind `sent`, and its block is not final yet. */
  awaitingBaseFinality: boolean;
  /** The support clock's anchor; also the meta line's "started" time. */
  startedAt: string;
  updatedAt: string;
  deadlineAt: string | null;
  estimateMs: number | null;
  amount: { requestedUsd: string | null; paidOutUsdc: string | null };
  links: {
    origin: { chainId: string; hash: string } | null;
    /** Null when `sent` was proven by the amount fallback. */
    payout: { hash: string } | null;
    delivery: { hash: string } | null;
  };
  /** How `sent` was proven: Fun's payout tx seen on Base, or a matching Base inflow alone. */
  sentBy: "payout_tx" | "amount_fallback" | null;
  delivery: FunPaymentDelivery | null;
  /**
   * One 1Click sweep can carry several Fun payments; the one with the earliest delivery evidence
   * owns it, and only the owner offers Confirm Deposit and replaces the sweep's own card.
   */
  isDeliveryOwner: boolean;
  /** Fun's raw checkout refund state, passed through. */
  funRefundState: string | null;
  nextAction: FunPaymentNextAction;
  dismissible: boolean;
  /** Changes whenever any other field does. */
  version: string;
}

export interface FunActivityResponse {
  accountAddress: string;
  items: FunPaymentItem[];
  /** Some item in `items` is not final yet. */
  anyOpen: boolean;
  /** The server still polls Fun for this account (a checkout opened within a day); keep reading. */
  serverPolling: boolean;
  /** Our last successful poll of Fun's API. */
  polledAt: string | null;
  /** The oldest of each collector's latest Base read; null with no collector or one never read. */
  baseReadAsOf: string | null;
  /** Sweeps that delivered any Fun payment of the account, listed or not; NEAR cards skip them. */
  claimedDeliveryIds: string[];
}

export type FunLookupResponse = { accountAddress: string } & (
  | { found: true; item: FunPaymentItem }
  | { found: false }
);

export interface FunPaymentStartRequest {
  clientRequestId: string;
  originAssetId: string;
  entry: FunPaymentEntry;
  /** Fun's form-of-payment type the user picked. */
  fiatMethod?: string;
  amountUsd?: number;
}

export interface FunPaymentStartResponse {
  accountAddress: string;
  /** Null when throttled: the address is still served and opening Fun is never blocked. */
  paymentId: string | null;
  originAssetId: string;
  /** Our display name for the chain. */
  chain: string;
  /** 1Click's chain key. */
  blockchain: string;
  /** 1Click's collector address. */
  depositAddress: string;
  /** 1Click's collector memo, set only on memo chains (Stellar, XRP, TON); null for Base. */
  depositMemo: string | null;
}

/**
 * Our names for Fun's widget callbacks: onConfirmation, onSuccess, onClose and onError. `failed`
 * also covers our own checkout start failures.
 */
export const FUN_CALLBACK_EVENTS = ["confirmation", "success", "closed", "failed"] as const;
export type FunCallbackEvent = (typeof FUN_CALLBACK_EVENTS)[number];

export interface FunPaymentObservedRequest {
  event: FunCallbackEvent;
  /** The opaque value Fun passes to onConfirmation; a source tx hash for Connect Wallet. */
  ref?: string;
  /** Optional client time of the callback; without it the server's receipt time orders it. */
  clientAt?: string;
}

export interface FunPaymentObservedResponse {
  accountAddress: string;
  item: FunPaymentItem | null;
}
